import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import format from 'date-fns/format';
import 'react-datepicker/dist/react-datepicker.css';

import Rating from 'react-rating';
import {
  CreateRatingInput,
  Hotel,
  HotelFragmentFragment,
  Mutation,
  MutationCreateRatingArgs,
} from '../../../../generated/types';
import { CustomStarEmpty, CustomStarFilled } from '../../../../config/icons';
import { useMutation } from '@apollo/client';
import { CREATE_RATING_QUERY } from '../../../../queries';
import { useStore } from '../../../../store/provider';

interface RatingFormValues {
  firstname: string;
  lastname: string;
  public_name: string;
  email: string;
  start_date: Date;
  end_date: Date;
  body: string;
  rating_kindness: number;
  rating_housing: number;
  rating_catering: number;
}

interface RatingFormProps {
  hotelId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  inline?: boolean;
}

export default function RatingForm({
  hotelId,
  isOpen,
  onClose,
  onSubmitted,
  inline,
}: RatingFormProps) {
  const toast = useToast();
  const [createRating, { loading }] = useMutation<
    Mutation,
    MutationCreateRatingArgs
  >(CREATE_RATING_QUERY);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
  } = useForm<RatingFormValues>();
  const ratingKindness = watch('rating_kindness');
  const ratingHousing = watch('rating_housing');
  const ratingCatering = watch('rating_catering');
  const start_date = watch('start_date');
  const end_date = watch('end_date');
  const store = useStore();

  const onSubmit = async (values: RatingFormValues) => {
    const apiValues: CreateRatingInput = {
      hotel: hotelId + '',
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      rating_catering: values.rating_catering,
      rating_housing: values.rating_housing,
      rating_kindness: values.rating_kindness,
      date_range: {
        value: format(values.start_date, 'yyyy-MM-dd'),
        end_value: format(values.end_date, 'yyyy-MM-dd'),
      },
      body: values.body,
      frontend: store.config.frontendName,
    };

    const result = await createRating({
      variables: {
        data: apiValues,
      },
    });

    if (result.data) {
      if (result.data.createRating && result.data.createRating.created) {
        toast({
          title: 'Bewertung erstellt',
          description: 'Vielen Dank für deine Bewertung!',
          status: 'success',
          duration: 6000,
          isClosable: true,
        });

        onSubmitted && onSubmitted();

        reset();
        onClose();
        return;
      } else {
        if (result.data.createRating?.errors) {
          result.data.createRating?.errors.forEach((error) => {
            setError(error.field as any, error);
          });
        }
      }
    }

    toast({
      title: 'Fehler',
      description:
        'Bei der Bewertung ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  };

  useEffect(() => {
    register('start_date');
    register('end_date');
    register('rating_housing');
    register('rating_catering');
    register('rating_kindness');
  }, []);

  const FormFields = () => {
    return (
      <>
        <FormControl
          display="flex"
          alignContent="center"
          isRequired
          marginTop={4}
          flex="1"
        >
          <FormLabel
            width="220px"
            paddingBottom={0}
            fontSize="sm"
            htmlFor="rating_catering"
          >
            Bewertung Verpflegung
          </FormLabel>
          <Rating
            initialRating={ratingCatering}
            onChange={(rating) => {
              setValue('rating_catering', rating);
            }}
            emptySymbol={<CustomStarEmpty fontSize="2xl" fill="yellow.500" />}
            fullSymbol={<CustomStarFilled fontSize="2xl" fill="yellow.500" />}
          />
        </FormControl>
        <FormControl
          display="flex"
          alignContent="center"
          isRequired
          marginTop={4}
          flex="1"
        >
          <FormLabel
            width="220px"
            paddingBottom={0}
            fontSize="sm"
            htmlFor="rating_housing"
          >
            Bewertung Unterkunft
          </FormLabel>
          <Rating
            initialRating={ratingHousing}
            onChange={(rating) => {
              setValue('rating_housing', rating);
            }}
            emptySymbol={<CustomStarEmpty fontSize="2xl" fill="yellow.500" />}
            fullSymbol={<CustomStarFilled fontSize="2xl" fill="yellow.500" />}
          />
        </FormControl>
        <FormControl
          display="flex"
          alignContent="center"
          isRequired
          marginTop={4}
          flex="1"
        >
          <FormLabel
            width="220px"
            paddingBottom={0}
            fontSize="sm"
            htmlFor="rating_kindness"
          >
            Bewertung Freundlichkeit
          </FormLabel>
          <Rating
            initialRating={ratingKindness}
            onChange={(rating) => {
              setValue('rating_kindness', rating);
            }}
            emptySymbol={<CustomStarEmpty fontSize="2xl" fill="yellow.500" />}
            fullSymbol={<CustomStarFilled fontSize="2xl" fill="yellow.500" />}
          />
        </FormControl>

        <Flex marginTop="8">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="firstname">
              Vorname
            </FormLabel>
            <Input
              size="sm"
              type="text"
              id="firstname"
              name="firstname"
              {...register('firstname', {
                required: true,
              })}
            />
          </FormControl>
          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="lastname">
              Nachname
            </FormLabel>
            <Input
              size="sm"
              type="text"
              id="lastname"
              name="lastname"
              {...register('lastname', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>

        <FormControl marginTop="4">
          <FormLabel fontSize="sm" htmlFor="public_name">
            Öffentlicher Name
          </FormLabel>
          <Input
            size="sm"
            type="text"
            id="public_name"
            name="public_name"
            {...register('public_name')}
          />
          <FormHelperText id="public_name-helper-text">
            Wenn Du einen öffentlichen Namen eingibst, wird dieser anstelle
            Vorname+Nachname in der Bewertung angezeigt.
          </FormHelperText>
        </FormControl>

        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="email">
              E-Mail
            </FormLabel>
            <Input
              name="email"
              size="sm"
              type="email"
              id="email"
              {...register('email', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>

        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="firstname">
              Aufenthaltszeitraum von (Datum)
            </FormLabel>

            <DatePicker
              selected={start_date}
              selectsStart
              startDate={start_date}
              endDate={end_date}
              onChange={(date) => {
                if (date && !Array.isArray(date)) {
                  setValue('start_date', date);
                }
              }}
              customInput={
                <Input
                  size="sm"
                  type="text"
                  id="start_date"
                  name="start_date"
                  readOnly
                  value={start_date ? format(start_date, 'dd.MM.yyyy') : ''}
                />
              }
              dateFormat="dd.MM.yyyy"
            />
          </FormControl>

          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="firstname">
              Aufenthaltszeitraum bis (Datum)
            </FormLabel>
            <DatePicker
              selected={end_date}
              selectsEnd
              startDate={start_date}
              endDate={end_date}
              minDate={start_date || null}
              onChange={(date) => {
                if (date && !Array.isArray(date)) {
                  setValue('end_date', date);
                }
              }}
              customInput={
                <Input
                  size="sm"
                  type="text"
                  id="end_date"
                  name="end_date"
                  readOnly
                  value={end_date ? format(end_date, 'dd.MM.yyyy') : ''}
                />
              }
              dateFormat="dd.MM.yyyy"
            />
          </FormControl>
        </Flex>
        <FormControl isRequired marginTop={4} flex="1">
          <FormLabel fontSize="sm" htmlFor="body">
            Text
          </FormLabel>
          <Textarea
            size="sm"
            id="body"
            name="body"
            {...register('body', {
              required: true,
            })}
          />
        </FormControl>
      </>
    );
  };

  if (inline) {
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFields />
        <Box mt={4}>
          <Button
            disabled={loading}
            isLoading={loading}
            width="100%"
            variant="outline"
            type="submit"
            colorScheme="blue"
          >
            Bewertung abschicken
          </Button>
        </Box>
      </form>
    );
  }

  return (
    <Modal
      closeOnOverlayClick={false}
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay zIndex={1100}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          {...{
            'data-track-content': '',
            'data-content-piece': 'Hotel Bewertungs Formular',
            'data-content-name': 'Hotel Detail',
          }}
        >
          <ModalContent>
            <ModalHeader>Bewertung erstellen</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormFields />
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                isDisabled={loading}
                isLoading={loading}
                type="submit"
                colorScheme="blue"
              >
                Bewertung abschicken
              </Button>
            </ModalFooter>
          </ModalContent>
        </form>
      </ModalOverlay>
    </Modal>
  );
}
